import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useLang, LANG_OPTIONS } from '../context/LanguageContext';
import { useNewsData } from '../context/NewsDataContext';
import BreakingNewsTicker from './BreakingNewsTicker';
import TextSizeToggle from './TextSizeToggle';
import './Header.css';

import { WHATSAPP_CHANNEL_URL } from '../config/env.js';
import { isNativeApp } from '../utils/platform.js';
import { BUSINESS } from '../config/business.js';

const UTIL_IDLE_MS = 30000;
const UTIL_TARGET_SEL = '.site-header__util-item, .text-stepper';

function SmartUtilBar({ className, children, preferAuth = true, lockSelector = null, ...rest }) {
  const rootRef = useRef(null);
  const idleTimerRef = useRef(null);
  const [idle, setIdle] = useState(false);
  const [thumb, setThumb] = useState({ left: 0, width: 0, ready: false });
  const litElRef = useRef(null);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const bumpActivity = () => {
    setIdle(false);
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => setIdle(true), UTIL_IDLE_MS);
  };

  const setLitClass = (el) => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll(UTIL_TARGET_SEL).forEach((node) => {
      node.classList.toggle('is-lit', node === el);
    });
    litElRef.current = el;
  };

  const placeThumb = (el) => {
    const root = rootRef.current;
    if (!root || !el) return;
    const rr = root.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    setThumb({
      left: er.left - rr.left,
      width: er.width,
      ready: true,
    });
    setLitClass(el);
  };

  const defaultTarget = () => {
    const root = rootRef.current;
    if (!root) return null;
    if (lockSelector) {
      const locked = root.querySelector(lockSelector);
      if (locked) return locked;
    }
    if (preferAuth) {
      return (
        root.querySelector('.site-header__util-item--signin') ||
        root.querySelector('.site-header__util-item--auth') ||
        root.querySelector('.site-header__util-item--lang')
      );
    }
    return root.querySelector('.site-header__util-item--lang');
  };

  const snapDefault = () => {
    const el = defaultTarget();
    if (el) placeThumb(el);
  };

  useEffect(() => {
    bumpActivity();
    const tmr = requestAnimationFrame(() => snapDefault());
    return () => {
      clearIdleTimer();
      cancelAnimationFrame(tmr);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, preferAuth, lockSelector]);

  useEffect(() => {
    const onResize = () => {
      if (litElRef.current && rootRef.current?.contains(litElRef.current)) {
        placeThumb(litElRef.current);
      } else {
        snapDefault();
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerMove = (e) => {
    bumpActivity();
    if (lockSelector) return;
    const target = e.target.closest(UTIL_TARGET_SEL);
    if (target && rootRef.current?.contains(target)) placeThumb(target);
  };

  const onFocusIn = (e) => {
    bumpActivity();
    const target = e.target.closest(UTIL_TARGET_SEL);
    if (target && rootRef.current?.contains(target)) placeThumb(target);
  };

  const onPointerLeave = () => {
    bumpActivity();
    snapDefault();
  };

  return (
    <div
      ref={rootRef}
      className={`site-header__util-bar site-header__util-bar--smart ${className || ''}${idle ? ' is-idle' : ''}`}
      onPointerMove={onPointerMove}
      onPointerDown={bumpActivity}
      onFocus={onFocusIn}
      onPointerLeave={onPointerLeave}
      {...rest}
    >
      <span
        className="site-header__util-thumb"
        aria-hidden
        style={{
          transform: `translateX(${thumb.left}px)`,
          width: `${thumb.width}px`,
          opacity: thumb.ready ? 1 : 0,
        }}
      />
      {children}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };
  const { lang, switchLang, t } = useLang();
  const { homeFeed } = useNewsData();
  const editionLive = homeFeed.editionLive;
  const nativeApp = isNativeApp();
  const [langOpen, setLangOpen] = useState(false);
  const langBtnRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const currentLang = LANG_OPTIONS.find((l) => l.code === lang);

  useEffect(() => {
    if (!langOpen || !langBtnRef.current) return;
    const update = () => {
      const rect = langBtnRef.current.getBoundingClientRect();
      const mobile = window.innerWidth < 640;
      if (mobile) {
        setMenuStyle({
          top: rect.bottom + 8,
          left: 12,
          right: 12,
          minWidth: 'unset',
        });
      } else {
        setMenuStyle({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
          minWidth: Math.max(rect.width, 172),
        });
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [langOpen]);

  useEffect(() => {
    if (!langOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [langOpen]);

  const langMenu = langOpen
    ? createPortal(
        <>
          <button
            type="button"
            className="site-header__backdrop"
            aria-label={t.closeBtn}
            onClick={() => setLangOpen(false)}
          />
          <div className="site-header__lang-panel site-header__menu--portal" style={menuStyle} role="listbox">
            {LANG_OPTIONS.map((l) => (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={lang === l.code}
                className={`site-header__lang-option${lang === l.code ? ' is-active' : ''}`}
                onClick={() => {
                  switchLang(l.code);
                  setLangOpen(false);
                }}
              >
                <span className="site-header__lang-badge">{l.short}</span>
                <span className="site-header__lang-option-label">{l.label}</span>
                {lang === l.code && (
                  <svg className="site-header__lang-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>,
        document.body
      )
    : null;

  const utilActions = (
    <>
      {WHATSAPP_CHANNEL_URL && (
        <>
          <a
            href={WHATSAPP_CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            className="site-header__util-item site-header__util-item--icon site-header__util-item--social site-header__util-item--wa"
            aria-label={t.whatsappChannel}
            title={t.whatsappChannel}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 0 0 .914.914l4.458-1.495A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
            </svg>
          </a>
          <span className="site-header__util-divider site-header__util-divider--before-lang" aria-hidden />
        </>
      )}

      <button
        ref={langBtnRef}
        type="button"
        className={`site-header__util-item site-header__util-item--lang${langOpen ? ' is-open' : ''}`}
        onClick={() => setLangOpen((o) => !o)}
        aria-expanded={langOpen}
        aria-haspopup="listbox"
        aria-label={`${t.statLangLabel}: ${currentLang?.label}`}
        title={`${t.statLangLabel}: ${currentLang?.label}`}
      >
        <svg className="site-header__lang-globe" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="site-header__lang-code">{currentLang?.short}</span>
      </button>

      <span className="site-header__util-divider" aria-hidden />

      {user?.isOwner && (
        <span className="site-header__preview-badge site-header__preview-badge--owner" title="Owner account">
          Owner
        </span>
      )}
      <TextSizeToggle />

      {user ? (
        <button
          type="button"
          className="site-header__util-item site-header__util-item--auth"
          onClick={handleLogout}
          aria-label={t.logout}
          title={t.logout}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span className="site-header__auth-label">{t.logout}</span>
        </button>
      ) : (
        <a
          href={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
          className="site-header__util-item site-header__util-item--auth site-header__util-item--signin"
        >
          {t.headerSignIn}
        </a>
      )}
    </>
  );

  return (
    <>
    <header className="site-header" id="site-header">
      {/* Mobile only — action buttons (no location/date strip) */}
      {!nativeApp && !isDesktop && (
        <div className="site-header__utility site-header__utility--mobile">
          <SmartUtilBar
            className="site-header__util-bar--actions site-header__util-bar--mobile"
            lockSelector={langOpen ? '.site-header__util-item--lang' : null}
          >
            {utilActions}
          </SmartUtilBar>
        </div>
      )}
    </header>

    <div className="site-header__nav" id="site-header-nav" data-sub-free>
      <div className="site-header__nav-inner">
        {/* Web: publisher left */}
        {!nativeApp && (
          <div className="site-header__publisher" title={`${BUSINESS.legalName} · GSTIN ${BUSINESS.gstin}`}>
            <span className="site-header__publisher-eyebrow">{t.poweredBy || 'Powered by'}</span>
            <span className="site-header__publisher-name">{BUSINESS.legalName}</span>
          </div>
        )}

        <div className="site-header__brand">
          <div className="site-header__logo-stage">
            <span className="site-header__logo-line" aria-hidden />
            <a href="#top" className="site-header__logo" aria-label={t.brandName}>
              <img src="/rk-logo.webp" alt="" />
            </a>
            <span className="site-header__logo-line" aria-hidden />
          </div>
          {!nativeApp && <p className="site-header__tagline">{t.headerTaglineFull}</p>}
        </div>

        {/* Web: utilities right */}
        {!nativeApp && isDesktop && (
          <SmartUtilBar
            className="site-header__util-bar--actions site-header__util-bar--desktop"
            aria-label="Account"
            lockSelector={langOpen ? '.site-header__util-item--lang' : null}
          >
            {utilActions}
          </SmartUtilBar>
        )}
      </div>

      {nativeApp && (
        <div className="site-header__native-actions">
          {editionLive && (
            <a href="#paper" className="site-header__native-btn site-header__native-btn--live">
              <span className="site-header__native-dot" aria-hidden />
              {t.headerEditionLive}
            </a>
          )}
          <button
            ref={langBtnRef}
            type="button"
            className="site-header__native-btn"
            onClick={() => setLangOpen((o) => !o)}
            aria-expanded={langOpen}
            aria-label={`${t.statLangLabel}: ${currentLang?.label}`}
          >
            🌐 {currentLang?.short}
          </button>
          {user ? (
            <button
              type="button"
              className="site-header__native-btn"
              onClick={handleLogout}
              aria-label={t.logout}
            >
              {t.logout}
            </button>
          ) : (
            <a href="/login" className="site-header__native-btn">
              {t.headerSignIn}
            </a>
          )}
        </div>
      )}
    </div>

    {langMenu}

    <div className="site-header__below">
      <BreakingNewsTicker />
    </div>
    </>
  );
}
